import React, { memo } from 'react';

import { getMobileNavInsertVisibility } from 'lib/getMobileNavInsertVisibility';

import { EventDayRecirc } from 'components/EventDayRecirc';
import { HeaderAndFooter } from 'components/services/HeaderAndFooter';

import { ContentNav } from './ContentNav';

import styles from './styles.module.scss';

/**
 * ArticleNav is a functional component that renders navigation elements
 * for an article, with support for chromeless mode and header/footer layout.
 *
 * @param {object} props - The component props.
 * @param {boolean} props.isChromeless - Determines if the navigation should be rendered without a header and footer.
 * @param {object} props.subnav - Configuration for the topic sub-navigation.
 * @param {object} props.tableOfContents - Configuration for the table of contents.
 * @param {string} props.articleId - The ID of the current article.
 * @param {object} props.eventDayRecirc - Configuration for the event day recirculation banner.
 * @param {React.ReactNode} props.children - The child elements to render within the navigation.
 * @param {string} props.hfsLayout - The layout type for the header and footer.
 * @returns {JSX.Element} The rendered ArticleNav component.
 */
export const ArticleNav = memo(({
  isChromeless,
  subnav,
  tableOfContents,
  articleId,
  eventDayRecirc,
  children,
  hfsLayout,
}) => {
  const articleContentNav = (
    <ContentNav
      subnav={subnav}
      tableOfContents={tableOfContents}
      articleId={articleId}
    />
  );

  if (isChromeless) {
    return (
      <>
        <div className={styles.navsContainer}>
          {articleContentNav}
        </div>
        {children}
      </>
    );
  }

  return (
    <HeaderAndFooter
      layout={hfsLayout}
      contentAboveHeader={(
        eventDayRecirc.isEnabled
        && (
          <EventDayRecirc
            headline={eventDayRecirc.headline}
            externalUrl={eventDayRecirc.externalUrl}
            content={eventDayRecirc.content}
          />
        )
      )}
      contentBelowHeader={articleContentNav}
      isNavbarSticky={!subnav.isEnabled}
      mobileNavInsertVisibility={getMobileNavInsertVisibility({
        subnavEnabled: subnav?.isEnabled,
        eventDayRecircEnabled: eventDayRecirc?.isEnabled,
        tocEnabled: tableOfContents?.isEnabled,
      })}
    >
      {children}
    </HeaderAndFooter>
  );
});
